import type { SSEEvent } from '../api/types.js';

/**
 * What the prompt field completes at the caret, as Claude Code does: a `/` command at the start of the message, or an
 * `@` path anywhere after a space. `start` is where the token begins (its `/` or `@`), `query` what follows it.
 */
export type Completion =
  | { kind: 'command'; start: number; query: string }
  | { kind: 'file'; start: number; query: string; dir: string; name: string };

export function completionAt(text: string, caret: number): Completion | null {
  const before = text.slice(0, caret);
  // A command: the whole message so far is `/word`
  const command = /^\/([\w.:-]*)$/.exec(before);
  if (command) return { kind: 'command', start: 0, query: command[1] };
  // A path: `@` at the start or after whitespace, then no whitespace up to the caret
  const file = /(^|\s)@([^\s@]*)$/.exec(before);
  if (!file) return null;
  const query = file[2];
  const slash = query.lastIndexOf('/');
  return { kind: 'file', start: caret - query.length - 1, query, dir: slash < 0 ? '' : query.slice(0, slash), name: query.slice(slash + 1) };
}

/** The message with the token at the caret replaced: a command or a file is followed by a space (unless one follows already), a folder by `/` */
export function applyCompletion(text: string, caret: number, c: Completion, value: string): { text: string; caret: number } {
  // No second space when one already follows
  const space = /^\s/.test(text.slice(caret)) ? '' : ' ';
  const inserted = c.kind === 'command' ? `/${value}${space}` : `@${value.endsWith(' ') && space === '' ? value.trimEnd() : value}`;
  const next = text.slice(0, c.start) + inserted + text.slice(caret);
  return { text: next, caret: c.start + inserted.length };
}

/** Commands matching what was typed: those that start with it first, then those that contain it */
export function matchCommands(commands: string[], query: string, max = 8): string[] {
  const q = query.toLowerCase();
  const starts = commands.filter((c) => c.toLowerCase().startsWith(q));
  const contains = commands.filter((c) => !c.toLowerCase().startsWith(q) && c.toLowerCase().includes(q));
  return [...starts, ...contains].slice(0, max);
}

/** The next message Claude Code proposes after its last turn; none once the operator wrote again */
export function latestSuggestion(events?: SSEEvent[]): { id: number; text: string } | null {
  for (let i = (events?.length ?? 0) - 1; i >= 0; i--) {
    const type = String(events![i].data.type ?? '');
    if (type === 'prompt_suggestion') {
      const text = String(events![i].data.suggestion ?? '');
      return text ? { id: events![i].id, text } : null;
    }
    // It comes after the turn's last output: anything said since makes it stale
    if (['user', 'human', 'assistant', 'text', 'tool_use', 'tool_result'].includes(type)) return null;
  }
  return null;
}
